import { Box, Typography, Divider, Grid, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoney } from './quotationUtils';
import { formatDateRange } from '../formatDate';

/**
 * Desglose completo de la simulación (mismo orden que la "Propuesta
 * económica" y la planilla de cálculo): haberes → descuentos → líquido →
 * costos de empresa → subtotal → margen → neto → IVA → total factura.
 */
const Line = ({ label, value, bold = false, highlight = false, indent = false }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: 0.25,
      pl: indent ? 2 : 0,
      ...(highlight && { backgroundColor: '#F4F2FF', borderRadius: 1, px: 1 }),
    }}
  >
    <Typography variant="body2" fontWeight={bold || highlight ? 700 : 400}>{label}</Typography>
    <Typography variant="body2" fontWeight={bold || highlight ? 700 : 400}>{value}</Typography>
  </Box>
);

// Etiqueta con la tasa aplicada (cesantía según la modalidad, fase 1b del
// 2026-10-13: OUT 0,6% / 2,4%, EST 0% / 3%); las simulaciones anteriores no la traen
const withRate = (label, rate) =>
  rate === null || rate === undefined
    ? label
    : `${label} (${String(parseFloat((Number(rate) * 100).toPrecision(6))).replace('.', ',')}%)`;

// hideSolvedNote: la columna del total de un período de varios meses no repite
// la nota del sueldo base resuelto desde el líquido (va en la del mes completo)
const SimulationBreakdown = ({ simulation, hideMonthlyReference = false, hideSolvedNote = false }) => {
  const { t } = useTranslation();
  if (!simulation) return null;

  const e = simulation.earnings || {};
  const d = simulation.legalDeductions || {};
  const c = simulation.employerCosts || {};
  const p = simulation.parameters || {};
  const money = (v) => formatMoney(v ?? 0);
  // Período parcial: los montos son proporcionales; el contrato lleva el mes
  // completo (monthlyEarnings, desde 2026-10-13). Una simulación desde el
  // líquido anterior a esa fecha traía el base inflado: hay que re-simular.
  const isPartial = Boolean(simulation.periodType) && simulation.periodType !== 'full_month';
  const solvedFromTarget = simulation.solvedBaseSalary !== null && simulation.solvedBaseSalary !== undefined;
  const staleInverse = solvedFromTarget && isPartial && !simulation.monthlyEarnings;

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {t('erp.quotations.breakdown.earnings')}
      </Typography>
      <Line label={t('erp.quotations.breakdown.baseSalary')} value={money(e.baseSalary)} />
      {solvedFromTarget && !staleInverse && !hideSolvedNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {isPartial
            ? t('erp.quotations.breakdown.solvedFromTargetPartial', {
                base: money(simulation.solvedBaseSalary),
                target: money(simulation.targetNetSalary),
              })
            : t('erp.quotations.breakdown.solvedFromTarget', { target: money(simulation.targetNetSalary) })}
        </Typography>
      )}
      {staleInverse && (
        <Typography variant="caption" sx={{ display: 'block', color: '#E65100' }}>
          {t('erp.quotations.breakdown.staleInverse')}
        </Typography>
      )}
      {isPartial && simulation.monthlyEarnings && !hideMonthlyReference && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('erp.quotations.breakdown.monthlyReference', {
            base: money(simulation.monthlyEarnings.baseSalary),
            gratification: money(simulation.monthlyEarnings.gratification),
          })}
        </Typography>
      )}
      {e.gratification > 0 && (
        <Line label={t('erp.quotations.breakdown.gratification')} value={money(e.gratification)} />
      )}
      {(e.taxableBonuses || []).map((b, i) => (
        <Line key={`b${i}`} label={b.name || t('erp.quotations.breakdown.bonus')} value={money(b.amount)} indent />
      ))}
      <Line label={t('erp.quotations.breakdown.totalTaxable')} value={money(simulation.taxableEarnings)} bold />
      {e.mealAllowance > 0 && <Line label={t('erp.quotations.form.mealAllowance')} value={money(e.mealAllowance)} />}
      {e.transportAllowance > 0 && (
        <Line label={t('erp.quotations.form.transportAllowance')} value={money(e.transportAllowance)} />
      )}
      {e.teleworkAllowance > 0 && (
        <Line label={t('erp.quotations.form.teleworkAllowance')} value={money(e.teleworkAllowance)} />
      )}
      {(e.otherAllowances || []).map((a, i) => (
        <Line key={`a${i}`} label={a.name || t('erp.quotations.breakdown.allowance')} value={money(a.amount)} indent />
      ))}
      <Line label={t('erp.quotations.breakdown.totalEarnings')} value={money(simulation.totalEarnings)} bold />

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {t('erp.quotations.breakdown.deductions')}
      </Typography>
      <Line label={t('erp.quotations.breakdown.pension')} value={money(d.pension)} />
      <Line label={t('erp.quotations.breakdown.health')} value={money(d.health)} />
      {d.unemploymentInsurance > 0 && (
        <Line label={withRate(t('erp.quotations.breakdown.unemploymentWorker'), p.unemploymentWorkerRate)} value={money(d.unemploymentInsurance)} />
      )}
      <Line label={t('erp.quotations.breakdown.incomeTax')} value={money(d.incomeTax)} />
      <Line label={t('erp.quotations.breakdown.totalDeductions')} value={money(d.total)} bold />
      <Line label={t('erp.quotations.sim.netSalary')} value={money(simulation.netSalary)} highlight />

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {t('erp.quotations.breakdown.employerCosts')}
      </Typography>
      <Line label={t('erp.quotations.breakdown.mutual')} value={money(c.mutual)} />
      <Line label={t('erp.quotations.breakdown.disabilityInsurance')} value={money(c.disabilityInsurance)} />
      <Line label={withRate(t('erp.quotations.breakdown.unemploymentEmployer'), p.unemploymentEmployerRate)} value={money(c.unemploymentEmployer)} />
      <Line label={t('erp.quotations.breakdown.lifeExpectancy')} value={money(c.lifeExpectancy)} />
      <Line label={t('erp.quotations.breakdown.individualCapitalization')} value={money(c.individualCapitalization)} />
      <Line label={t('erp.quotations.breakdown.protectedProfit')} value={money(c.protectedProfit)} />
      <Line
        label={simulation.vacationProvisionOverridden
          ? `${t('erp.quotations.breakdown.vacationProvision')} ${t('erp.quotations.breakdown.vacationAgreed')}`
          : t('erp.quotations.breakdown.vacationProvision')}
        value={money(c.vacationProvision)}
      />
      {/* Provisiones del acuerdo o de la cotización (fase 4b, 2026-10-13):
          calculadas (aviso previo, indemnización, reemplazo de vacaciones) y
          de monto fijo. Las simulaciones anteriores no las traen. */}
      {c.noticeProvision > 0 && (
        <Line label={t('erp.quotations.breakdown.noticeProvision')} value={money(c.noticeProvision)} />
      )}
      {c.severanceProvision > 0 && (
        <Line label={t('erp.quotations.breakdown.severanceProvision')} value={money(c.severanceProvision)} />
      )}
      {c.vacationReplacementProvision > 0 && (
        <Line label={t('erp.quotations.breakdown.vacationReplacementProvision')} value={money(c.vacationReplacementProvision)} />
      )}
      {(c.agreementProvisions || []).map((a, i) => {
        const name = a.name || t('erp.quotations.breakdown.agreementProvision');
        return (
          <Line key={`ap${i}`} value={money(a.amount)}
            label={a.prorated ? `${name} (${t('erp.quotations.detail.prorated')})` : name} />
        );
      })}
      {/* Gastos operacionales: desde la fase 4 salen del acuerdo; si no los trae, no aparecen */}
      {c.operationalExpenses > 0 && (
        <Line label={t('erp.quotations.breakdown.operationalExpenses')} value={money(c.operationalExpenses)} />
      )}
      {(c.additionalProvisions || []).map((a, i) => (
        <Line key={`p${i}`} label={a.name || t('erp.quotations.breakdown.provision')} value={money(a.amount)} indent />
      ))}
      {c.adminExpense > 0 && (
        <Line label={t('erp.quotations.breakdown.adminExpense')} value={money(c.adminExpense)} />
      )}
      {c.assistanceCost > 0 && (
        <Line label={t('erp.quotations.breakdown.assistanceCost')} value={money(c.assistanceCost)} />
      )}
      <Line label={t('erp.quotations.breakdown.totalCosts')} value={money(c.total)} bold />

      <Divider sx={{ my: 1 }} />
      <Line label={t('erp.quotations.sim.subtotal')} value={money(simulation.subtotal)} bold />
      <Line
        label={
          simulation.margin?.feeType === 'percentage' && simulation.margin?.value != null
            ? `${t('erp.quotations.sim.marginAmount')} (${simulation.margin.value}%)`
            : t('erp.quotations.sim.marginAmount')
        }
        value={money(simulation.marginAmount)}
      />
      {simulation.marginMinimumApplied && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('erp.quotations.breakdown.marginMinimumApplied')}
        </Typography>
      )}
      <Line label={t('erp.quotations.breakdown.netTotal')} value={money(simulation.netTotal)} bold />
      <Line label={t('erp.quotations.breakdown.vat')} value={money(simulation.vat)} />
      <Line label={t('erp.quotations.sim.invoiceTotal')} value={money(simulation.invoiceTotal)} highlight />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('erp.quotations.breakdown.footer', {
          days: simulation.days,
          period: simulation.parameterSetPeriod || '—',
        })}
      </Typography>
    </Box>
  );
};

// 'YYYY-MM' → 'MM/AAAA'
const formatMonth = (month) => {
  const [year, m] = String(month).split('-');
  return `${m}/${year}`;
};

/**
 * Mensual y proporcional lado a lado (fase 2 del cotizador OUT/EST,
 * 2026-10-13 — minuta: "ver mensual y período a la vez"). En un período
 * parcial la simulación trae `monthly` (mes completo: lo que va al contrato y
 * a Talana); el período real es la puesta a disposición del mes de ingreso.
 * Mes completo o simulación anterior sin `monthly`: un solo desglose.
 *
 * Período de varios meses (2026-10-31, decisión de Erwin: mostrar el mensual y
 * el total): mes de ingreso, mes completo (contrato; × N meses o solo
 * referencia si no hay), mes de término y total del período (`segments` de
 * la simulación, cuyo primer nivel es el total).
 */
export const SimulationView = ({ simulation, period }) => {
  const { t } = useTranslation();
  if (!simulation) return null;

  // Fase 1b (2026-10-13): la cotización usa los parámetros del mes de
  // ingreso; si ese mes no tenía, el backend usó otro y lo marca. Las
  // simulaciones anteriores (sin la marca: usaban siempre el último set) se
  // comparan con la fecha de ingreso.
  const entryMonth = simulation.periodMonth || (period?.startDate ? String(period.startDate).slice(0, 7) : null);
  const usedOtherMonth = simulation.parameterSetFallback !== undefined
    ? simulation.parameterSetFallback === true
    : Boolean(entryMonth && simulation.parameterSetPeriod && simulation.parameterSetPeriod !== entryMonth);
  const fallbackNotice = usedOtherMonth ? (
    <Alert severity="warning" sx={{ mb: 1 }}>
      {t('erp.quotations.breakdown.parameterSetFallback', {
        used: simulation.parameterSetPeriod || '—',
        month: entryMonth || '—',
      })}
    </Alert>
  ) : null;

  const isPartial = Boolean(simulation.periodType) && simulation.periodType !== 'full_month';
  if (!isPartial) {
    return (
      <>
        {fallbackNotice}
        <SimulationBreakdown simulation={simulation} />
      </>
    );
  }

  if (simulation.periodType === 'multi_period' && simulation.segments && simulation.monthly) {
    const { entry, full, exit } = simulation.segments;
    const fullMonths = (full && full.months) || [];
    // Sin tope de duración: más de 3 meses van como rango (igual que el PDF)
    const fullMonthsLabel = fullMonths.length <= 3
      ? fullMonths.map(formatMonth).join(', ')
      : `${formatMonth(fullMonths[0])} – ${formatMonth(fullMonths[fullMonths.length - 1])}`;
    const segmentHint = (segment) =>
      t('erp.quotations.breakdown.segmentColumnHint', {
        period: formatDateRange(segment.startDate, segment.endDate),
        days: segment.days,
      });
    const columns = [
      entry && { key: 'entry', title: t('erp.quotations.breakdown.entryColumn'), hint: segmentHint(entry), sim: entry.simulation },
      {
        key: 'monthly',
        title: t('erp.quotations.breakdown.monthlyColumn'),
        hint: fullMonths.length > 0
          ? t('erp.quotations.breakdown.fullMonthsHint', { n: fullMonths.length, months: fullMonthsLabel })
          : t('erp.quotations.breakdown.fullMonthsReference'),
        sim: simulation.monthly,
      },
      exit && { key: 'exit', title: t('erp.quotations.breakdown.exitColumn'), hint: segmentHint(exit), sim: exit.simulation },
      {
        key: 'total',
        title: t('erp.quotations.breakdown.totalColumn'),
        hint: t('erp.quotations.breakdown.totalColumnHint', {
          period: period?.startDate ? formatDateRange(period.startDate, period.endDate) : '',
          days: simulation.days,
        }),
        sim: simulation,
      },
    ].filter(Boolean);

    return (
      <>
        {fallbackNotice}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('erp.quotations.breakdown.multiPeriodNote')}
        </Typography>
        <Grid container spacing={3}>
          {columns.map((column) => (
            <Grid item xs={12} md={6} lg={12 / columns.length} key={column.key}>
              <Typography variant="subtitle2" fontWeight={700}>
                {column.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {column.hint}
              </Typography>
              <SimulationBreakdown simulation={column.sim} hideMonthlyReference hideSolvedNote={column.key === 'total'} />
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  if (!simulation.monthly) {
    return (
      <>
        {fallbackNotice}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('erp.quotations.breakdown.noMonthly')}
        </Typography>
        <SimulationBreakdown simulation={simulation} />
      </>
    );
  }

  const periodLabel = period?.startDate ? formatDateRange(period.startDate, period.endDate) : '';

  return (
    <>
      {fallbackNotice}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('erp.quotations.breakdown.monthlyColumn')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('erp.quotations.breakdown.monthlyColumnHint')}
          </Typography>
          <SimulationBreakdown simulation={simulation.monthly} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('erp.quotations.breakdown.partialColumn', { period: periodLabel })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('erp.quotations.breakdown.partialColumnHint', { days: simulation.days })}
          </Typography>
          <SimulationBreakdown simulation={simulation} hideMonthlyReference />
        </Grid>
      </Grid>
    </>
  );
};

export default SimulationBreakdown;
